import { useState, useCallback, SetStateAction, Dispatch } from 'react';
import { DateTime } from "luxon";
import useAlert from '../Alerts/useAlert';
import { useHttpUtils } from './useHttpUtils';
import { Quality } from '../Types/LotDetailQuality';

export interface AlternativeGradeReviewResult {
    gradeName: string,
    violation: boolean,
    warnings: string[],
    awaitingResults: boolean
}

export const useGrades = (businessUnitCode : string) => {

    const { setAlert } = useAlert();
    const { fetchDataAuthenticated } = useHttpUtils();

    const [loading, setLoading] = useState(false);
    const [grades, setGrades] = useState([]);

    const getGrades = useCallback((businessUnitCode : string) => {
        if (!loading && businessUnitCode) {
            setLoading(true);

            fetchDataAuthenticated(`api/v1/grade?businessUnitCode=${businessUnitCode}`)
                .then(response => {
                    if (response.ok) {
                        return response.json();
                    } else {
                        throw new Error("Failed to get grades for business unit");
                    }
                })
                .then(data => setGrades(data))
                .catch(error => {
                    setAlert("danger", "Error", error.message);
                })
                .finally(() => setLoading(false));
        }
    }, [fetchDataAuthenticated, loading, setAlert]);

    return { grades, loading, getGrades };
}

export const useAlternativeGrades = (lotNumber: string) => {

    const { setAlert } = useAlert();
    const { fetchDataAuthenticated } = useHttpUtils();

    const [loading, setLoading] = useState(false);
    const [alternativeGrades, setAlternativeGrades] = useState([]);

    const getAlternativeGrades = useCallback((lotNumber : string) => {
        if (!loading) {
            setLoading(true);

            fetchDataAuthenticated(`api/v1/grade/lot?lotNumber=${lotNumber}`)
                .then(response => {
                    if (response.ok) {
                        return response.json();
                    } else {
                        throw new Error("Failed to get alternative grades for lot");
                    }
                })
                .then(data => setAlternativeGrades(data))
                .catch(error => {
                    setAlert("danger", "Error", error.message);
                })
                .finally(() => setLoading(false));
        }
    }, [fetchDataAuthenticated, setAlert]);

    return { alternativeGrades, loading, getAlternativeGrades };
}

export const useReviewAlternativeGrades = () => {

    const { setAlert } = useAlert();
    const { fetchDataAuthenticated } = useHttpUtils();

    const [loading, setLoading] = useState(false);

    const reviewAlternativeGrades = useCallback((lotNumber: string, grades: string[], setReviewedGrades: Dispatch<SetStateAction<AlternativeGradeReviewResult[]>>) => {
        if (!loading) {
            setLoading(true);

            let gradesList = grades.join(",");

            fetchDataAuthenticated(`api/v1/grade/lot/${lotNumber}/review?grades=${gradesList}`)
                .then(response => {
                    if (response.ok) {
                        return response.json();
                    } else {
                        throw new Error("Failed to get review status for grades for lot");
                    }
                })
                .then(data => setReviewedGrades(data))
                .catch(error => {
                    setAlert("danger", "Error", error.message);
                })
                .finally(() => setLoading(false));
        }
    }, [fetchDataAuthenticated, setAlert]);

    const reviewAlternativeGradesForSplitPackageLot = useCallback((lotNumber: string, grades: string[], splitStartUtc: DateTime,
        splitEndUtc: DateTime, qualities: Quality[], setReviewedGrades: Dispatch<SetStateAction<AlternativeGradeReviewResult[]>>) => {
        if (!loading) {
            setLoading(true);

            let gradesList = grades.join(",");

            var requestBody: any = {
                splitStartUtc: splitStartUtc.toISO(),
                splitEndUtc: splitEndUtc.toISO()
            };

            if (qualities.length > 0) {
                requestBody.qualities = qualities;
            }

            fetchDataAuthenticated(`api/v1/grade/lot/${lotNumber}/splitpackages/review?&grades=${gradesList}`, "POST", JSON.stringify(requestBody))
                .then(response => {
                    if (response.ok) {
                        return response.json();
                    } else {
                        throw new Error("Failed to get review status for grades for lot");
                    }
                })
                .then(data => setReviewedGrades(data))
                .catch(error => {
                    setAlert("danger", "Error", error.message);
                })
                .finally(() => setLoading(false));
        }
    }, [fetchDataAuthenticated, setAlert]);

    

    return { loading, reviewAlternativeGrades, reviewAlternativeGradesForSplitPackageLot };
}